// One call-control path for the UI. A call this device's engine tracks is controlled through
// the engine (it owns the SIP leg, expected_version and idempotency for its commands). Any
// other call falls back to server-only commands. Answer never falls back: without this
// device's own verified offer there is no audio to answer with.
import type { Call } from '@bcio-voice/contracts';
import { copyForCode } from '../lib/errorCopy.ts';
import { engineErrorText } from '../lib/userCopy.ts';
import { type BridgeCall, type BridgeResult, type BridgeSnapshot, type CallBridge, bridgeCallLive, engineCallFor } from './bridge.ts';
import { type ActionName, type CommandController, commandStatusText } from './commands.ts';

const PENDING = new Set(['sending', 'accepted', 'dispatching', 'unconfirmed', 'unknown']);

/** The live engine call attached to this server call, if any. */
export function liveEngineCall(snapshot: BridgeSnapshot, call: Call): BridgeCall | null {
  const ec = engineCallFor(snapshot, call.id);
  return ec && bridgeCallLive(ec) ? ec : null;
}

/** A user command (not the outbound create) is still awaiting confirmation. */
export function isPending(snapshot: BridgeSnapshot, commands: CommandController, call: Call): boolean {
  const ec = liveEngineCall(snapshot, call);
  const engineBusy = !!ec?.command && ec.command.kind !== 'create' && PENDING.has(ec.command.phase);
  return engineBusy || commands.isBusy(call.id);
}

export interface StatusView {
  text: string;
  tone: 'pending' | 'unknown' | 'failed';
  canCheckAgain: boolean;
  canDismiss: boolean;
  requestId: string | null;
}

const VERB: Record<string, string> = { create: 'Starting call', answer: 'Answering', reject: 'Declining', hangup: 'Ending call', hold: 'Holding', resume: 'Resuming', dtmf: 'Sending keys' };

export function statusFor(snapshot: BridgeSnapshot, commands: CommandController, call: Call, actionError: string | null): StatusView | null {
  if (actionError) return { text: actionError, tone: 'failed', canCheckAgain: false, canDismiss: true, requestId: null };
  const ec = liveEngineCall(snapshot, call);
  if (ec?.command) {
    const { kind, phase, error } = ec.command;
    if (kind === 'create' && (phase === 'accepted' || phase === 'dispatching' || phase === 'applied' || phase === 'sending')) return null;
    if (phase === 'sending' || phase === 'accepted' || phase === 'dispatching') return { text: `${VERB[kind] ?? 'Working'}…`, tone: 'pending', canCheckAgain: false, canDismiss: false, requestId: null };
    if (phase === 'unconfirmed' || phase === 'unknown') return { text: 'Not confirmed yet. Checking with the phone system…', tone: 'unknown', canCheckAgain: false, canDismiss: false, requestId: null };
    if (phase === 'conflict') return { text: 'The call changed. Review it, then try again.', tone: 'failed', canCheckAgain: false, canDismiss: false, requestId: null };
    if (phase === 'rejected' || phase === 'failed') return { text: copyForCode(error?.code ?? '', 'The phone system didn’t accept that.'), tone: 'failed', canCheckAgain: false, canDismiss: false, requestId: null };
  }
  const cmd = commands.get(call.id);
  const text = commandStatusText(cmd);
  if (!cmd || !text) return null;
  return {
    text,
    tone: cmd.phase === 'failed' ? 'failed' : cmd.phase === 'unknown' ? 'unknown' : 'pending',
    canCheckAgain: cmd.phase === 'unknown',
    canDismiss: cmd.phase === 'failed',
    requestId: cmd.error?.requestId ?? null,
  };
}

/** Runs a control. Returns a plain-language error, or null on success or when ignored. */
export async function runAction(bridge: CallBridge, snapshot: BridgeSnapshot, commands: CommandController, call: Call, action: ActionName, digits?: string): Promise<string | null> {
  const ec = liveEngineCall(snapshot, call);
  if (ec) {
    let result: BridgeResult;
    switch (action) {
      case 'answer':
        result = await bridge.answer(ec.key);
        break;
      case 'reject':
        result = await bridge.decline(ec.key);
        break;
      case 'hangup':
        result = await bridge.hangup(ec.key);
        break;
      case 'hold':
        result = await bridge.hold(ec.key);
        break;
      case 'resume':
        result = await bridge.resume(ec.key);
        break;
      default:
        result = await bridge.sendDtmf(ec.key, digits ?? '');
    }
    // Command phases (unconfirmed, conflict, rejected) are shown from the engine snapshot.
    if (result.ok || ['command_unconfirmed', 'server_conflict', 'server_rejected'].includes(result.code)) return null;
    return engineErrorText(result.code, result.serverCode);
  }
  if (action === 'answer') return 'This call hasn’t reached this browser yet.';
  await commands.act(call, action, digits);
  return null;
}
